<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
    <scroll-to-top v-if="enableScrollToTop" />
    <ReportProgress />
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import ReportProgress from '@core/components/ReportProgress.vue'

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";
import { TokenService } from "./apiServices/storageService";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

import moment from "moment-timezone";
moment.tz.setDefault("UTC");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    ScrollToTop,
    ReportProgress,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
  data() {
    return {
      getNotificationInterval: null,
    };
  },
  mounted() {
    window.addEventListener('abort-report', this.handleAbortReport)
  },

  beforeDestroy() {
    window.removeEventListener('abort-report', this.handleAbortReport)
  },

  methods: {
    handleAbortReport() {
      this.$store.commit('report/REQUEST_CANCEL_INTERNAL')
    },
  },
  watch: {
    "$store.state.user.user_authenticated": function (newVal) {
      if (!newVal) {
        const path = window.location.pathname;
        const resetPasswordRegex = /^\/resetpassword/;
        if (path !== "/" && !resetPasswordRegex.test(path)) window.location.replace("/");
        if (this.getNotificationInterval) {
          clearInterval(this.getNotificationInterval);
        }
        return;
      }

      if (this.getNotificationInterval) {
        clearInterval(this.getNotificationInterval);
      }
      this.getNotificationInterval = setInterval(() => {
        // this.$store.dispatch("user/setNewNotifications");
      }, 10000);
      // resetPasswordRegex.test(window.location.pathname)
      const resetPasswordRegex = /^\/resetpassword/;
      if (window.location.pathname === "/") this.$router.push({ name: "home" });
    },
  },
  async beforeMount() {
    const token = TokenService.getToken();
    if (token) {
      await this.$store.dispatch("user/setUserDetails");
    } else {
      const path = window.location.pathname;
      const resetPasswordRegex = /^\/resetpassword/;
      if (path !== "/" && !resetPasswordRegex.test(path)) {
        window.location.replace("/");
      }
    }
  },
};
</script>
